import React from "react";
import { Play } from "@phosphor-icons/react";

/**
 * Reusable 16:9 (default) video embed for Synthesia / YouTube / Vimeo iframes.
 *
 * Props:
 *  - src: iframe URL (required) — e.g. https://share.synthesia.io/embeds/videos/{id}
 *  - title: accessible title (required)
 *  - eyebrow, heading, description: optional copy shown above the video
 *  - aspectRatio: string, defaults to "16/9"
 *  - testId: optional data-testid
 */
export default function VideoEmbed({
  src,
  title,
  eyebrow,
  heading,
  description,
  aspectRatio = "16/9",
  testId,
}) {
  return (
    <div data-testid={testId || "video-embed"}>
      {(eyebrow || heading || description) && (
        <div className="max-w-3xl">
          {eyebrow && <div className="eyebrow flex items-center gap-2 text-bronze-dark">
            <Play size={14} weight="fill" /> {eyebrow}
          </div>}
          {heading && (
            <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl mt-3 leading-tight">
              {heading}
            </h3>
          )}
          {description && (
            <p className="mt-4 text-ink-muted leading-relaxed">{description}</p>
          )}
        </div>
      )}
      <div
        className="mt-6 relative overflow-hidden rounded-md border border-bronze-light/40 bg-ink"
        style={{ aspectRatio }}
      >
        <iframe
          src={src}
          title={title}
          loading="lazy"
          allowFullScreen
          allow="encrypted-media; fullscreen; microphone; screen-wake-lock;"
          className="absolute inset-0 w-full h-full border-0"
        />
      </div>
    </div>
  );
}
